import { useRegionStore } from '@/store/region'
import { type LangEnum, supportedLangsByName } from '@/constants/locales'

export function useRegion() {
  const regionStore = useRegionStore()
  const regionId = computed(() => regionStore.currentRegionId ?? 0)
  const regionName = computed(() => regionStore.selectedRegionOption?.name ?? '')

  const getNextRegionRouteRawById = (regionId: number) => {
    return { path: `/regions/${regionId}` }
  }

  return { regionId, regionName, getNextRegionRouteRawById }
}

/**
 * 這個 composable 是在 middleware 中使用，請不要在裡面使用 server side 不允許的，例如 `useRoute`、`useI18n`。
 */
export function useRegionChecker() {
  const { $i18n, $pinia } = useNuxtApp()

  function detectValidLang(currentLangCode: LangEnum) {
    const { locale, localeProperties } = $i18n
    const regionStore = useRegionStore($pinia)

    const currentRegionSupportedLangs = regionStore.currentRegion?.languages ?? []
    // 因 BE language 中的欄位 `code` 是 FE 的 `name`，故需要先找當前語言的 `name` 是什麼
    const currentLangName = localeProperties.value.name
    const isCurrentLangValid = currentRegionSupportedLangs.some(l => l.code === currentLangName)
    const fallbackLang = supportedLangsByName[currentRegionSupportedLangs[0]?.code ?? '']?.code as LangEnum
    const supportedLang = isCurrentLangValid ? currentLangCode : fallbackLang

    if (!supportedLang) {
      logInDev('== supportedLang not found ==', { currentRegion: regionStore.currentRegion })
    }

    return { isCurrentLangValid, supportedLang: supportedLang ?? locale.value }
  }

  return { detectValidLang }
}

/**
 * id 優先權：url id > 最後活躍的 tab 的 id > cookie id > detected id
 * 舉例：
 *  - 有兩個 tab，一個是 region 1，一個是 region 2，使用者在 region 1 的 tab 時，活躍的 tab 是 region 1 的，故 id 都會是 1，即使重整。但當使用者切到 region 2 的 tab 時，活躍的 tab 變成 region 2，故 id 會變成 2。
 *  - 延上，如果此時新開一個 tab 訪問不包含 region id 的 ETG 網址，則會使用上一次活躍的 tab 的 id，即 region 2 (假設瀏覽器沒被關閉過)。
 *  - 延上，如果先關閉瀏覽器，再新開一個 tab 訪問不包含 region id 的 ETG 網址，則會以 detected id 為主。
 */
export function useRegionIdHandler() {
  const { regionId } = useRegion()
  const regionIdCookie = useRegionIdCookie()

  const visibility = useDocumentVisibility()

  function changeCookieRegionIdIfNeeded(newRegionId: number) {
    if (regionIdCookie.value !== newRegionId) {
      regionIdCookie.value = newRegionId
    }
  }

  watch(visibility, newVisibility => {
    if (newVisibility === 'visible') {
      changeCookieRegionIdIfNeeded(regionId.value)
    }
  })

  watch(regionId, changeCookieRegionIdIfNeeded, { immediate: true })
}
